'use client';

import Image from 'next/image';
import { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import type { TabItem, StaticSectionType } from '../static';
import AttachmentListTableSection from './AttachmentListTableSection';
import AttachmentSliderSection from './AttachmentSliderSection';
import IconGridSection from './IconGridSection';
import CardGridSection from './CardGridSection';
import { ChecklistContentSection } from './ChecklistContentSection';
import ButtonGroupSection from './ButtonGroupSection';
import { GrayCardGridSection } from './GrayCardGridSection';
import { YouTubeVideoSection } from './YouTubeVideoSection';
import PhotoCardGridSection from './PhotoCardGridSection';
import AccordionSection from './AccordionSection';
import { OrangeVisionSection } from './OrangeVisionSection';

interface TabSectionProps {
  heading?: string;
  tab_items: TabItem[];
  add_background?: boolean;
  add_bottom_border?: boolean;
}

function TabSectionItem({ section, idx }: { section: StaticSectionType; idx: number }) {
  if (section.type === 'attachment_list_table' && section.data?.attachment_list_table_items) {
    return (
      <AttachmentListTableSection
        heading={section.data.heading}
        items={section.data.attachment_list_table_items}
        show_category_column={section.data.show_category_column}
        show_year_column={section.data.show_year_column}
        show_file_size_column={section.data.show_file_size_column}
        striped={section.data.striped}
        bordered={section.data.bordered}
        add_background={section.data.add_background}
        add_bottom_border={section.data.add_bottom_border}
      />
    );
  }

  if (section.type === 'attachment_slider' && section.data?.attachment_slider_items) {
    return (
      <AttachmentSliderSection
        heading={section.data.heading}
        description={section.data.description}
        items={section.data.attachment_slider_items}
        visibleItems={section.data.visible_items}
        add_background={section.data.add_background}
        add_bottom_border={section.data.add_bottom_border}
      />
    );
  }

  if (section.type === 'icon_grid_section' && section.data?.grid_items) {
    return (
      <IconGridSection
        heading={section.data.heading}
        grid_items={section.data.grid_items}
        columns={section.data.columns}
        add_background={section.data.add_background}
      />
    );
  }

  if (section.type === 'card_grid_section' && section.data?.cards) {
    return (
      <CardGridSection
        heading={section.data.heading}
        cards={section.data.cards}
        columns={section.data.columns}
        card_style={section.data.card_style as 'default' | 'bordered' | 'elevated' | undefined}
        add_background={section.data.add_background}
        add_bottom_border={section.data.add_bottom_border}
      />
    );
  }

  if (section.type === 'checklist_content' && section.data) {
    return (
      <ChecklistContentSection
        content={section.data.content}
        list={section.data.list || []}
        add_background={section.data.add_background}
        add_bottom_border={section.data.add_bottom_border}
      />
    );
  }

  if (section.type === 'button_group_section' && section.data?.buttons) {
    return (
      <ButtonGroupSection
        heading={section.data.heading}
        buttons={section.data.buttons}
        add_background={section.data.add_background}
        add_bottom_border={section.data.add_bottom_border}
      />
    );
  }

  if (section.type === 'gray_card_grid_section' && section.data?.cards) {
    return (
      <GrayCardGridSection
        cards={section.data.cards.filter(
          (card): card is { icon: string; description: string } =>
            typeof card.description === 'string' && card.description.length > 0
        )}
        columns={section.data.columns}
        add_background={section.data.add_background}
      />
    );
  }

  if (section.type === 'photo_card_grid_section' && section.data?.photo_cards) {
    return (
      <PhotoCardGridSection
        heading={section.data.heading}
        cards={section.data.photo_cards}
        columns={section.data.columns}
        add_background={section.data.add_background}
        add_bottom_border={section.data.add_bottom_border}
      />
    );
  }

  if (section.type === 'accordion_section' && section.data?.accordion_items) {
    return (
      <AccordionSection
        heading={section.data.heading}
        items={section.data.accordion_items}
        add_background={section.data.add_background}
        add_bottom_border={section.data.add_bottom_border}
      />
    );
  }

  if (section.type === 'orange_vision_section' && section.data?.main_quote) {
    return (
      <OrangeVisionSection
        mainQuote={section.data.main_quote}
        add_background={section.data.add_background}
      />
    );
  }

  if (section.video) {
    return (
      <YouTubeVideoSection
        video={section.video}
        add_background={section.data?.add_background}
      />
    );
  }

  // Inline renders for basic types
  return (
    <div key={idx} className="flex flex-col gap-3 w-full">
      {section.heading && (() => {
        const level = section.heading_level || 'h2';
        const alignment = section.heading_alignment || 'left';
        const alignmentClass = { left: 'text-left', center: 'text-center', right: 'text-right' }[alignment];
        const sizeClass = { h1: 'text-4xl', h2: 'text-3xl', h3: 'text-2xl', h4: 'text-xl', h5: 'text-lg', h6: 'text-base' }[level];
        const className = `text-primary-700 ${sizeClass} font-bold ${alignmentClass}`;
        switch (level) {
          case 'h1': return <h1 className={className}>{section.heading}</h1>;
          case 'h2': return <h2 className={className}>{section.heading}</h2>;
          case 'h3': return <h3 className={className}>{section.heading}</h3>;
          case 'h4': return <h4 className={className}>{section.heading}</h4>;
          case 'h5': return <h5 className={className}>{section.heading}</h5>;
          case 'h6': return <h6 className={className}>{section.heading}</h6>;
          default: return <h2 className={className}>{section.heading}</h2>;
        }
      })()}

      {section.content && typeof section.content === 'string' && (
        section.content.trim().startsWith('<') ? (
          <div
            className="text-black font-normal text-[18px] [&_p]:mb-[18px] [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_a]:text-primary-700 [&_a]:underline overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_th]:bg-primary-700 [&_th]:text-white [&_th]:p-3 [&_th]:text-left [&_th]:align-middle [&_th_p]:my-0 [&_td]:p-3 [&_td]:border [&_td]:border-gray-200 [&_td_p]:my-0 [&_tbody_tr:nth-child(even)]:bg-gray-50 [&_tbody_tr:nth-child(odd)]:bg-white"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        ) : (
          <p className="text-black font-normal text-[18px]">{section.content}</p>
        )
      )}

      {section.image && (
        <div className={`w-full flex ${section.imagePosition === 'center' || section.imageNotFull ? 'justify-center' : section.imagePosition === 'right' ? 'justify-end' : 'justify-start'}`}>
          <Image
            src={typeof section.image === 'string' ? section.image : section.image.url}
            alt={(section.imageAlt || section.heading) ?? 'SMF'}
            width={section.imageSize?.width ?? 800}
            height={section.imageSize?.height ?? 600}
            unoptimized
            className={`h-auto object-contain ${section.size || ''}`}
          />
        </div>
      )}
    </div>
  );
}

export default function TabSection({ heading, tab_items, add_background, add_bottom_border }: TabSectionProps) {
  const items = tab_items ?? [];
  const toTabValue = (label: string) => label.toLowerCase().replace(/\s+/g, '-') || 'tab-0';
  const firstTab = items[0] ? toTabValue(items[0].label) : 'tab-0';
  const validValues = items.map((t) => toTabValue(t.label));

  const [activeTab, setActiveTab] = useState(firstTab);

  useEffect(() => {
    const getTabFromHash = () => {
      const hash = window.location.hash.replace('#', '');
      return validValues.includes(hash) ? hash : firstTab;
    };

    setActiveTab(getTabFromHash());

    const onHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    history.replaceState(null, '', `#${value}`);
  }, []);

  if (items.length === 0) return null;

  const wrapper = (children: React.ReactNode) => (
    add_background ? (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[40vw] bg-[linear-gradient(to_bottom,rgba(237,237,249,0),#DEE5E8)] mb-8 py-8">
        <div className="container max-w-7xl mx-auto px-10 flex flex-col gap-6">
          {children}
        </div>
      </section>
    ) : (
      <div className={`w-full flex flex-col gap-6 ${add_bottom_border ? 'border-b border-gray-200 pb-10' : ''}`}>
        {children}
      </div>
    )
  );

  return wrapper(
    <>
      {heading && (
        <h2 className="text-primary-700 text-3xl font-bold">{heading}</h2>
      )}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-fit">
          {items.map((tab) => (
            <TabsTrigger
              key={tab.label}
              id={toTabValue(tab.label)}
              value={toTabValue(tab.label)}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {items.map((tab) => (
          <TabsContent
            key={tab.label}
            value={toTabValue(tab.label)}
          >
            <div className="flex flex-col gap-8 pt-4">
              {tab.sections.map((section, idx) => (
                <TabSectionItem key={idx} section={section} idx={idx} />
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
}
